/**
 * Price calculation utilities with platform and credit card fees
 * 
 * Fee structure:
 * - Platform fee: 2% of base price, capped at $30
 * - Credit card fee: Gross-up for Stripe processing (2.9% + $0.30)
 */

/**
 * Calculate the total price including all fees
 * @param basePrice - The base price before fees
 * @returns The total price including platform and credit card fees
 */
export function calculatePriceWithFees(basePrice: number): number {
  if (basePrice <= 0) return 0;

  // Platform fee: 2% of base price, capped at $30
  let platformFee = parseFloat((0.02 * basePrice).toFixed(2));
  if (platformFee > 30) {
    platformFee = 30;
  }

  // Add platform fee to base
  const totalWithPlatformFee = basePrice + platformFee;

  // Credit card fee: Gross-up for Stripe 2.9% + $0.30
  const total = parseFloat(((totalWithPlatformFee + 0.30) / (1 - 0.029)).toFixed(2));
  
  return total;
}

/**
 * Format a single price with fees as just the amount (no label)
 * @param basePrice - The base price before fees
 * @returns Formatted string like "$52.83" or "Free"
 */
export function formatPriceAmountWithFees(basePrice: number): string {
  if (basePrice <= 0) return 'Free';
  
  const totalWithFees = calculatePriceWithFees(basePrice);
  return `$${totalWithFees.toFixed(2)}`;
}

/**
 * Format a single price with fees as a display string (inline format)
 * @param basePrice - The base price before fees
 * @returns Formatted string like "$52.83 incl. fees" or "Free"
 */
export function formatPriceWithFees(basePrice: number): string {
  if (basePrice <= 0) return 'Free';
  
  return `${formatPriceAmountWithFees(basePrice)} incl. fees`;
}

/**
 * Format a price range with fees as just the amount (no label)
 * @param minPrice - The minimum base price
 * @param maxPrice - The maximum base price
 * @returns Formatted string like "$27.13-$80.63" or "Free"
 */
export function formatPriceRangeAmountWithFees(minPrice: number, maxPrice: number): string {
  if (minPrice <= 0 && maxPrice <= 0) return 'Free';
  
  const minWithFees = calculatePriceWithFees(minPrice);
  const maxWithFees = calculatePriceWithFees(maxPrice);
  
  if (minWithFees === maxWithFees) {
    return minWithFees === 0 ? 'Free' : `$${minWithFees.toFixed(2)}`;
  }
  
  if (minPrice <= 0) {
    return `$0-$${maxWithFees.toFixed(2)}`;
  }
  
  return `$${minWithFees.toFixed(2)}-$${maxWithFees.toFixed(2)}`;
}

/**
 * Format a price range with fees as a display string (inline format)
 * @param minPrice - The minimum base price
 * @param maxPrice - The maximum base price
 * @returns Formatted string like "$27.13-$80.63 incl. fees" or "Free"
 */
export function formatPriceRangeWithFees(minPrice: number, maxPrice: number): string {
  if (minPrice <= 0 && maxPrice <= 0) return 'Free';
  
  const amount = formatPriceRangeAmountWithFees(minPrice, maxPrice);
  return amount === 'Free' ? 'Free' : `${amount} incl. fees`;
}

/**
 * Format an add-on price (with + prefix) as just the amount (no label)
 * @param basePrice - The base price before fees
 * @returns Formatted string like "+$10.28" or "Free"
 */
export function formatAddonPriceAmountWithFees(basePrice: number): string {
  if (basePrice <= 0) return 'Free';
  
  const totalWithFees = calculatePriceWithFees(basePrice);
  return `+$${totalWithFees.toFixed(2)}`;
}

/**
 * Format an add-on price (with + prefix) including fees (inline format)
 * @param basePrice - The base price before fees
 * @returns Formatted string like "+$10.28 incl. fees"
 */
export function formatAddonPriceWithFees(basePrice: number): string {
  if (basePrice <= 0) return 'Free';
  
  return `${formatAddonPriceAmountWithFees(basePrice)} incl. fees`;
}

/**
 * Get breakdown of fees for a given base price
 * @param basePrice - The base price before fees
 * @returns Object with platformFee, creditCardFee, and total
 */
export function getFeeBreakdown(basePrice: number) {
  if (basePrice <= 0) {
    return {
      platformFee: 0,
      creditCardFee: 0,
      total: 0,
    };
  }

  let platformFee = parseFloat((0.02 * basePrice).toFixed(2));
  if (platformFee > 30) {
    platformFee = 30;
  }

  const totalWithPlatformFee = basePrice + platformFee;
  const total = parseFloat(((totalWithPlatformFee + 0.30) / (1 - 0.029)).toFixed(2));
  const creditCardFee = parseFloat((total - totalWithPlatformFee).toFixed(2));

  return {
    platformFee,
    creditCardFee,
    total,
  };
}

/**
 * Get the minimum base unit cost for a sticker (before fees).
 * Used for shop cart subtotal and add-to-cart pricing.
 */
export function getStickerMinimumUnitCost(sticker: any): number {
  if (!sticker) return 0;

  if (sticker.campaignType === 'Ticket' && sticker.priceRange) {
    const rangeMatch = String(sticker.priceRange).match(
      /\$?(\d+(?:\.\d+)?)\s*[-–]\s*\$?(\d+(?:\.\d+)?)/,
    );
    if (rangeMatch) {
      return parseFloat(rangeMatch[1]);
    }
    const singleMatch = String(sticker.priceRange).match(/\$?(\d+(?:\.\d+)?)/);
    if (singleMatch) {
      return parseFloat(singleMatch[1]);
    }
  }

  if (sticker.campaignType === 'Ticket' && sticker.varients?.length > 0) {
    const costs = sticker.varients
      .map((variant: any) => Number(variant.cost))
      .filter((cost: number) => !Number.isNaN(cost) && cost > 0);
    if (costs.length > 0) {
      return Math.min(...costs);
    }
  }

  if (
    sticker.campaignType === 'Sweepstakes'
    && sticker.sweepstakesSticker?.entryCost?.length > 0
  ) {
    const costs = sticker.sweepstakesSticker.entryCost
      .map((cost: number) => Number(cost))
      .filter((cost: number) => !Number.isNaN(cost) && cost >= 0);
    if (costs.length > 0) {
      return Math.min(...costs);
    }
  }

  if (Array.isArray(sticker.quickCosts) && sticker.quickCosts.length > 0) {
    const costs = sticker.quickCosts
      .map((cost: number) => Number(cost))
      .filter((cost: number) => !Number.isNaN(cost) && cost > 0);
    if (costs.length > 0) {
      return Math.min(...costs);
    }
  }

  if (Number(sticker.cost) > 0) {
    return Number(sticker.cost);
  }

  return 0;
}
